import { useEffect, useState } from 'react';
import {
  fetchSalesPipelinesSummary,
  fetchNextDueAbandoned,
  fetchNextDueOrderItems,
  fetchOverdueAbandoned,
  fetchOverdueOrderItems,
  fetchSalesPipelineAppointments,
  SalesPipelinesSummary,
  NextDueRow,
  Paginated,
  SalesPipelineAppointmentRow,
} from '../../api/dashboard';
import { Appointment, fetchAppointments } from '../../api/appointments';

type Bucket = 'thisWeek' | 'nextWeek' | 'thisMonth' | 'nextMonth';

function CardPager({
  page,
  totalPages,
  onPrev,
  onNext,
}: {
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="mt-2 flex items-center justify-end gap-2 text-[11px] text-slate-500">
      <button
        type="button"
        className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        disabled={page <= 1}
        onClick={onPrev}
      >
        Prev
      </button>
      <span className="self-center">
        Page {page} / {totalPages}
      </span>
      <button
        type="button"
        className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        disabled={page >= totalPages}
        onClick={onNext}
      >
        Next
      </button>
    </div>
  );
}

export default function SalesPipelinesPage() {
  const [data, setData] = useState<SalesPipelinesSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stateByBucket, setStateByBucket] = useState<Record<Bucket, {
    items: NextDueRow[];
    abPage: number;
    oiPage: number;
    loadingMore: boolean;
  }>>({
    thisWeek: { items: [], abPage: 1, oiPage: 1, loadingMore: false },
    nextWeek: { items: [], abPage: 1, oiPage: 1, loadingMore: false },
    thisMonth: { items: [], abPage: 1, oiPage: 1, loadingMore: false },
    nextMonth: { items: [], abPage: 1, oiPage: 1, loadingMore: false },
  });
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [calendarEvents, setCalendarEvents] = useState<
    Record<string, { overdue: number; appointments: number }>
  >({}); // key: YYYY-MM-DD -> counts
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null); // YYYY-MM-DD in UTC
  const [dayOverdues, setDayOverdues] = useState<NextDueRow[] | null>(null);
  const [dayAppointments, setDayAppointments] = useState<Appointment[] | null>(
    null,
  );
  const [dayLoading, setDayLoading] = useState(false);
  const [dayError, setDayError] = useState<string | null>(null);
  const [selectedDue, setSelectedDue] = useState<NextDueRow | null>(null);
  const [appointmentsByBucket, setAppointmentsByBucket] = useState<Record<
    Bucket,
    { items: SalesPipelineAppointmentRow[]; page: number; totalPages: number }
  >>({
    thisWeek: { items: [], page: 1, totalPages: 1 },
    nextWeek: { items: [], page: 1, totalPages: 1 },
    thisMonth: { items: [], page: 1, totalPages: 1 },
    nextMonth: { items: [], page: 1, totalPages: 1 },
  });

  useEffect(() => {
    let mounted = true;
    const buckets: Bucket[] = ['thisWeek', 'nextWeek', 'thisMonth', 'nextMonth'];
    Promise.all([
      fetchSalesPipelinesSummary(),
      // Fetch enough rows once; we'll paginate on the client at 5 per page
      ...buckets.map((b) => fetchNextDueAbandoned(1, 200, b)),
      ...buckets.map((b) => fetchNextDueOrderItems(1, 200, b)),
    ])
      .then(([summary, abThis, abNext, abThisM, abNextM, oiThis, oiNext, oiThisM, oiNextM]) => {
        if (!mounted) return;
        setData(summary);
        const build = (ab: Paginated<NextDueRow>, oi: Paginated<NextDueRow>) => {
          const merged = [...(ab?.data ?? []), ...(oi?.data ?? [])].sort(
            (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
          );
          return {
            items: merged,
            abPage: 1,
            oiPage: 1,
            loadingMore: false,
          } as any;
        };
        setStateByBucket({
          thisWeek: build(abThis, oiThis),
          nextWeek: build(abNext, oiNext),
          thisMonth: build(abThisM, oiThisM),
          nextMonth: build(abNextM, oiNextM),
        });
        setLoading(false);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e?.message || 'Failed to load sales pipelines');
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const loadAppointmentsForBucket = (bucket: Bucket, page: number) => {
    fetchSalesPipelineAppointments(bucket, page, 5)
      .then((resp) => {
        setAppointmentsByBucket((prev) => ({
          ...prev,
          [bucket]: {
            items: resp.data,
            page: resp.meta.page,
            totalPages: resp.meta.totalPages,
          },
        }));
      })
      .catch(() => {
        setAppointmentsByBucket((prev) => ({
          ...prev,
          [bucket]: { ...prev[bucket], items: [] },
        }));
      });
  };

  useEffect(() => {
    (['thisWeek', 'nextWeek', 'thisMonth', 'nextMonth'] as Bucket[]).forEach(
      (bucket) => {
        loadAppointmentsForBucket(bucket, appointmentsByBucket[bucket].page);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Load overdue items for current calendar month using UTC month boundaries
    const y = calendarMonth.getFullYear();
    const m = calendarMonth.getMonth();
    const from = new Date(Date.UTC(y, m, 1, 0, 0, 0, 0));
    const to = new Date(Date.UTC(y, m + 1, 0, 23, 59, 59, 999));
    const fromISO = from.toISOString();
    const toISO = to.toISOString();
    Promise.all([
      fetchOverdueAbandoned(fromISO, toISO),
      fetchOverdueOrderItems(fromISO, toISO),
      fetchAppointments({ page: 1, limit: 500, from: fromISO, to: toISO }),
    ])
      .then(([ab, oi, ap]) => {
        const map: Record<string, { overdue: number; appointments: number }> =
          {};
        const bump = (iso: string, key: 'overdue' | 'appointments') => {
          const day = iso.slice(0, 10);
          const prev = map[day] || { overdue: 0, appointments: 0 };
          prev[key] += 1;
          map[day] = prev;
        };
        (ab?.data ?? []).forEach((r) => bump(r.dueDate, 'overdue'));
        (oi?.data ?? []).forEach((r) => bump(r.dueDate, 'overdue'));
        (ap?.data ?? []).forEach((a: Appointment) =>
          bump(a.scheduledAt, 'appointments'),
        );
        setCalendarEvents(map);
      })
      .catch(() => setCalendarEvents({}));
  }, [calendarMonth]);

  const renderCalendar = () => {
    const y = calendarMonth.getFullYear();
    const m = calendarMonth.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const startWeekday = (first.getDay() + 6) % 7; // Monday=0
    const daysInMonth = last.getDate();
    const cells: (Date | null)[] = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
    while (cells.length % 7 !== 0) cells.push(null);

    const monthLabel = first.toLocaleString(undefined, { month: 'long', year: 'numeric' });

    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">
            Overdue &amp; Appointment Calendar
          </div>
          <div className="flex items-center gap-2">
            <button
              className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
              onClick={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
            >
              ◀
            </button>
            <div className="min-w-[10rem] text-center text-sm font-medium text-slate-700">{monthLabel}</div>
            <button
              className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
              onClick={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
            >
              ▶
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-2 text-center text-xs font-medium text-slate-500">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
            <div key={d} className="py-1">{d}</div>
          ))}
        </div>
        <div className="mt-2 grid grid-cols-7 gap-2">
          {cells.map((date, idx) => {
            if (!date) return <div key={idx} className="h-20 rounded-lg bg-slate-50" />;
            const dayKey = new Date(
              Date.UTC(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
              ),
            )
              .toISOString()
              .slice(0, 10);
            const counts = calendarEvents[dayKey] || {
              overdue: 0,
              appointments: 0,
            };
            const totalCount = counts.overdue + counts.appointments;
            const isToday = (() => { const t = new Date(); return t.toDateString() === date.toDateString(); })();
            return (
              <button
                key={idx}
                type="button"
                className={`h-20 rounded-lg border ${counts.overdue > 0
                  ? 'border-rose-300 bg-rose-50 hover:bg-rose-100'
                  : counts.appointments > 0
                    ? 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100'
                    : 'border-slate-200 bg-white'
                  } p-2 text-left transition`}
                onClick={() => {
                  if (!totalCount) return;
                  setSelectedDate(date);
                  setSelectedDayKey(dayKey);
                  setDayLoading(true);
                  setDayError(null);
                  // Query precise UTC day boundaries matching our keys
                  const y = date.getFullYear();
                  const m = date.getMonth();
                  const d = date.getDate();
                  const from = new Date(Date.UTC(y, m, d, 0, 0, 0, 0));
                  const to = new Date(Date.UTC(y, m, d, 23, 59, 59, 999));
                  const fromISO = from.toISOString();
                  const toISO = to.toISOString();
                  Promise.all([
                    fetchOverdueAbandoned(fromISO, toISO, 1, 500),
                    fetchOverdueOrderItems(fromISO, toISO, 1, 500),
                    fetchAppointments({
                      page: 1,
                      limit: 100,
                      from: fromISO,
                      to: toISO,
                    }),
                  ])
                    .then(([ab, oi, ap]) => {
                      const merged = [...(ab?.data ?? []), ...(oi?.data ?? [])].sort(
                        (a, b) => a.dueDate.localeCompare(b.dueDate),
                      );
                      setDayOverdues(merged);
                      setDayAppointments(ap?.data ?? []);
                      setDayLoading(false);
                    })
                    .catch((e) => {
                      setDayError(
                        e?.message || 'Failed to load overdues/appointments',
                      );
                      setDayLoading(false);
                    });
                }}
              >
                <div className={`text-[11px] ${isToday ? 'font-semibold text-primary' : 'text-slate-500'}`}>{date.getDate()}</div>
                {counts.overdue > 0 && (
                  <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-[2px] text-[10px] font-semibold text-rose-700">
                    {counts.overdue} overdue
                  </div>
                )}
                {counts.appointments > 0 && (
                  <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-[2px] text-[10px] font-semibold text-emerald-700">
                    {counts.appointments} appt
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const formatAmount = (value: number | null | undefined) => {
    const numeric = typeof value === 'number' ? value : Number(value ?? 0);
    if (Number.isNaN(numeric)) return '0.00';
    return numeric.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const appointmentRevenueByBucket: Record<Bucket, number> = {
    thisWeek:
      appointmentsByBucket.thisWeek.items.reduce(
        (sum: number, r: SalesPipelineAppointmentRow) =>
          sum + (r.estimatedTotal ?? 0),
        0,
      ) || 0,
    nextWeek:
      appointmentsByBucket.nextWeek.items.reduce(
        (sum: number, r: SalesPipelineAppointmentRow) =>
          sum + (r.estimatedTotal ?? 0),
        0,
      ) || 0,
    thisMonth:
      appointmentsByBucket.thisMonth.items.reduce(
        (sum: number, r: SalesPipelineAppointmentRow) =>
          sum + (r.estimatedTotal ?? 0),
        0,
      ) || 0,
    nextMonth:
      appointmentsByBucket.nextMonth.items.reduce(
        (sum: number, r: SalesPipelineAppointmentRow) =>
          sum + (r.estimatedTotal ?? 0),
        0,
      ) || 0,
  };

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-danger">{error}</div>;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-slate-800">Sales Pipeline</h2>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
        {([
          { key: 'thisWeek', label: 'This Week', accent: 'bg-primary/10 text-primary' },
          { key: 'nextWeek', label: 'Next Week', accent: 'bg-emerald-100 text-emerald-700' },
          { key: 'thisMonth', label: 'This Month', accent: 'bg-amber-100 text-amber-700' },
          { key: 'nextMonth', label: 'Next Month', accent: 'bg-fuchsia-100 text-fuchsia-700' },
        ] as { key: Bucket; label: string; accent: string }[]).map(({ key, label, accent }) => (
          <div key={key} className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-1">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-700">{label}</div>
                <div className={`rounded-full px-2 py-1 text-xs font-semibold ${accent}`}>
                  {(() => {
                    const s = data ?? { thisWeek: 0, nextWeek: 0, thisMonth: 0, nextMonth: 0 };
                    return s[key];
                  })()}
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50/70 p-2 text-xs text-slate-600">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                {`Estimated revenue${data?.currencySymbol ? ` (in ${data.currencySymbol})` : ''}`}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: '100%', factor: 1 },
                  { label: '80%', factor: 0.8 },
                  { label: '60%', factor: 0.6 },
                  { label: '40%', factor: 0.4 },
                ].map(({ label: pctLabel, factor }) => (
                  <div
                    key={pctLabel}
                    className="flex items-baseline justify-between rounded border border-slate-200 bg-white px-2 py-1"
                  >
                    <span className="text-[11px] font-medium text-slate-500">{pctLabel}</span>
                    <span className="text-[11px] font-semibold text-slate-800">
                      {formatAmount((data?.revenue?.[key] ?? 0) * factor)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-1 divide-y divide-slate-100">
              {(() => {
                const st = stateByBucket[key];
                const pageSize = 5;
                const currentPage = Math.max(st.abPage || 1, st.oiPage || 1);
                const start = (currentPage - 1) * pageSize;
                const end = start + pageSize;
                const pageItems = (st.items ?? []).slice(start, end);
                return pageItems;
              })().map((row) => (
                <button key={row.id} type="button" className="w-full py-2 text-left hover:bg-slate-50"
                  onClick={() => setSelectedDue(row)}
                >
                  <div className="flex items-center justify-between px-1">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-slate-800">{row.customerName || '—'}</div>
                      <div className="truncate text-xs text-slate-500">
                        {row.orderNumber ? `#${row.orderNumber}` : ''} {row.productName ? `• ${row.productName}` : ''}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-xs font-semibold text-primary">{new Date(row.dueDate).toLocaleDateString()}</div>
                      <div className="text-[11px] text-slate-500">in {row.daysLeft} day{row.daysLeft === 1 ? '' : 's'}</div>
                    </div>
                  </div>
                </button>
              ))}
              {stateByBucket[key].items.length === 0 && (
                <div className="py-2 text-center text-xs text-slate-500">No upcoming activities.</div>
              )}
            </div>
            {(() => {
              const st = stateByBucket[key];
              const pageSize = 5;
              const currentPage = Math.max(st.abPage || 1, st.oiPage || 1);
              const start = (currentPage - 1) * pageSize;
              const end = start + pageSize;
              const pageItems = (st.items ?? []).slice(start, end);
              // Always keep visual room for up to 5 rows
              if (pageItems.length === 0) {
                // Already show "No upcoming activities." above; add blank spacers to preserve height
                return Array.from({ length: 4 }).map((_, idx) => (
                  <div key={`sp-${idx}`} className="py-2" />
                ));
              }
              if (pageItems.length >= 5) return null;
              return Array.from({ length: 5 - pageItems.length }).map((_, idx) => (
                <div key={`ph-${idx}`} className="py-2">
                  <div className="flex items-center justify-between opacity-60">
                    <div className="min-w-0">
                      <div className="h-4 w-40 rounded bg-slate-100" />
                      <div className="mt-1 h-3 w-28 rounded bg-slate-100" />
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="h-3 w-16 rounded bg-slate-100" />
                      <div className="mt-1 h-3 w-14 rounded bg-slate-100" />
                    </div>
                  </div>
                </div>
              ));
            })()}
            {(() => {
              const st = stateByBucket[key];
              const pageSize = 5;
              const currentPage = Math.max(st.abPage || 1, st.oiPage || 1);
              const totalItems = st.items.length;
              const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
              return (
                <div className="pt-2 mt-auto">
                  <CardPager
                    page={currentPage}
                    totalPages={totalPages}
                    onPrev={() => {
                      if (currentPage <= 1) return;
                      const targetPage = currentPage - 1;
                      setStateByBucket((prev) => ({
                        ...prev,
                        [key]: { ...prev[key], abPage: targetPage, oiPage: targetPage },
                      }));
                    }}
                    onNext={() => {
                      if (currentPage >= totalPages) return;
                      const targetPage = currentPage + 1;
                      setStateByBucket((prev) => ({
                        ...prev,
                        [key]: { ...prev[key], abPage: targetPage, oiPage: targetPage },
                      }));
                    }}
                  />
                </div>
              );
            })()}
          </div>
        ))}
      </div>

      <h3 className="mt-6 text-sm font-semibold text-slate-800">
        Appointment Pipeline
      </h3>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
        {([
          { key: 'thisWeek', label: 'This Week' },
          { key: 'nextWeek', label: 'Next Week' },
          { key: 'thisMonth', label: 'This Month' },
          { key: 'nextMonth', label: 'Next Month' },
        ] as { key: Bucket; label: string }[]).map(({ key, label }) => (
          <div
            key={key}
            className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm min-h-[260px]"
          >
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-700">
                {label}
              </div>
              <div className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                {appointmentsByBucket[key].items.length}
              </div>
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50/70 p-2 text-xs text-slate-600">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Estimated revenue
                {/* same currency idea as main summary; omit symbol for now */}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: '100%', factor: 1 },
                  { label: '80%', factor: 0.8 },
                  { label: '60%', factor: 0.6 },
                  { label: '40%', factor: 0.4 },
                ].map(({ label: pctLabel, factor }) => (
                  <div
                    key={pctLabel}
                    className="flex items-baseline justify-between rounded border border-slate-200 bg-white px-2 py-1"
                  >
                    <span className="text-[11px] font-medium text-slate-500">
                      {pctLabel}
                    </span>
                    <span className="text-[11px] font-semibold text-slate-800">
                      {formatAmount(appointmentRevenueByBucket[key] * factor)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-1 space-y-2">
              {appointmentsByBucket[key].items.length === 0 ? (
                <div className="text-xs text-slate-400">
                  No appointments in this bucket.
                </div>
              ) : (
                appointmentsByBucket[key].items.map((row) => (
                  <div
                    key={row.appointmentId}
                    className="flex items-center justify-between rounded border border-slate-100 bg-slate-50 px-2 py-1 text-xs"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium text-slate-800">
                        {row.customerName || 'Customer'}
                      </div>
                      <div className="truncate text-[11px] text-slate-500">
                        {new Date(row.scheduledAt).toLocaleString()} •{' '}
                        {row.locationName || '—'}
                      </div>
                    </div>
                    <a
                      href={`/appointments/${row.appointmentId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 inline-flex items-center justify-center rounded border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:border-primary/30 hover:text-primary"
                    >
                      View
                    </a>
                  </div>
                ))
              )}
            </div>
            <div className="mt-2 flex items-center justify-end gap-2 text-[11px] text-slate-500">
              <button
                type="button"
                className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={appointmentsByBucket[key].page <= 1}
                onClick={() => {
                  const nextPage = Math.max(
                    1,
                    appointmentsByBucket[key].page - 1,
                  );
                  loadAppointmentsForBucket(key, nextPage);
                }}
              >
                Prev
              </button>
              <span className="self-center">
                Page {appointmentsByBucket[key].page} /
                {appointmentsByBucket[key].totalPages}
              </span>
              <button
                type="button"
                className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={
                  appointmentsByBucket[key].page >=
                  appointmentsByBucket[key].totalPages
                }
                onClick={() => {
                  const nextPage = Math.min(
                    appointmentsByBucket[key].totalPages,
                    appointmentsByBucket[key].page + 1,
                  );
                  loadAppointmentsForBucket(key, nextPage);
                }}
              >
                Next
              </button>
            </div>
          </div>
        ))}
      </div>

      {renderCalendar()}

      {selectedDate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-800">
                Overdues on {selectedDate.toLocaleDateString()} {selectedDayKey ? `(UTC ${selectedDayKey})` : ''}
              </div>
              <button
                type="button"
                className="rounded border border-slate-300 bg-white px-2 py-1 text-sm hover:bg-slate-50"
                onClick={() => {
                  setSelectedDate(null);
                  setDayOverdues(null);
                  setDayAppointments(null);
                  setDayError(null);
                }}
              >
                ✕
              </button>
            </div>
            {dayLoading && <div className="p-3 text-sm text-slate-500">Loading...</div>}
            {dayError && <div className="p-3 text-sm text-danger">{dayError}</div>}
            {!dayLoading && !dayError && (
              <div className="max-h-[60vh] space-y-4 overflow-auto">
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Overdues
                  </div>
                  <div className="divide-y divide-slate-100">
                    {(dayOverdues ?? []).map((row) => (
                      <button
                        key={row.id}
                        type="button"
                        className="flex w-full items-center justify-between py-2 text-left hover:bg-slate-50"
                        onClick={() => {
                          setSelectedDue(row);
                          setSelectedDate(null);
                        }}
                      >
                        <div className="min-w-0 px-1">
                          <div className="truncate text-sm font-medium text-slate-800">
                            {row.customerName || '—'}
                          </div>
                          <div className="truncate text-xs text-slate-500">
                            {row.orderNumber ? `#${row.orderNumber}` : ''}{' '}
                            {row.productName ? `• ${row.productName}` : ''}
                          </div>
                        </div>
                        <div className="shrink-0 px-1 text-right">
                          <div className="text-xs font-semibold text-primary">
                            {new Date(row.dueDate).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </div>
                        </div>
                      </button>
                    ))}
                    {(!dayOverdues || dayOverdues.length === 0) && (
                      <div className="p-3 text-center text-sm text-slate-500">
                        No overdues for this day.
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Appointments
                  </div>
                  <div className="divide-y divide-slate-100">
                    {(dayAppointments ?? []).map((appt) => (
                      <a
                        key={appt.id}
                        href={`/appointments/${appt.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex w-full items-center justify-between py-2 text-left hover:bg-slate-50"
                      >
                        <div className="min-w-0 px-1">
                          <div className="truncate text-sm font-medium text-slate-800">
                            {appt.customer
                              ? `${appt.customer.firstName ?? ''} ${appt.customer.lastName ?? ''
                                }`.trim() || appt.customer.mobileNumber || '—'
                              : '—'}
                          </div>
                          <div className="truncate text-xs text-slate-500">
                            {new Date(appt.scheduledAt).toLocaleTimeString(
                              [],
                              { hour: '2-digit', minute: '2-digit' },
                            )}{' '}
                            • {appt.location?.name || '—'}
                          </div>
                        </div>
                      </a>
                    ))}
                    {(!dayAppointments || dayAppointments.length === 0) && (
                      <div className="p-3 text-center text-sm text-slate-500">
                        No appointments for this day.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedDue && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-800">Due Details</div>
              <button type="button" className="rounded border border-slate-300 bg-white px-2 py-1 text-sm hover:bg-slate-50" onClick={() => setSelectedDue(null)}>✕</button>
            </div>
            <div className="space-y-2 text-sm">
              <div><span className="text-slate-500">Customer:</span> <span className="font-medium text-slate-800">{selectedDue.customerName || '—'}</span></div>
              <div><span className="text-slate-500">Order #:</span> <span className="font-medium text-slate-800">{selectedDue.orderNumber || '—'}</span></div>
              <div><span className="text-slate-500">Item:</span> <span className="font-medium text-slate-800">{selectedDue.productName || '—'}</span></div>
              <div><span className="text-slate-500">Due Date:</span> <span className="font-medium text-slate-800">{new Date(selectedDue.dueDate).toLocaleString()}</span></div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <a
                href={`/order-management/${selectedDue.orderId}`}
                className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
              >
                View Order
              </a>
              <button type="button" className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50" onClick={() => setSelectedDue(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
